import { FC, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { type Dayjs } from 'dayjs'
import cn from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import { InputNew, InputTextarea } from '@UI/InputNew'
import { IcoRemove } from '@/utils/icons'
import type { Manager } from '@/api/types/crm/crm.types'
import type { CrmCreateLeadFunnelTaskRequest } from '@/api/types/crm/crmLeadTasks.types'
import DatePicker from '@pages/Admin/CRM/components/base/DatePicker/DatePicker'
import Select from '@pages/Admin/CRM/components/base/Select/Select'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import {
  leadTaskCreateRequiresManagerId,
  resolveManagerUserId,
} from './leadTasks.utils'
import classes from './index.module.css'

/** Выше модалки карточки лида (default Modal zIndex = 103). */
const CREATE_LEAD_TASK_MODAL_Z_INDEX = 110

type CreateLeadTaskModalProps = {
  open: boolean
  onClose: () => void
  leadFunnelId: number
  managers: Manager[]
  /** `crm_managers.id` активной связки — маппим в `user.id`. */
  committedCrmManagerId: number | null
  role: string | null | undefined
  submitting?: boolean
  onSubmit: (body: CrmCreateLeadFunnelTaskRequest) => Promise<unknown>
}

const CreateLeadTaskModal: FC<CreateLeadTaskModalProps> = ({
  open,
  onClose,
  leadFunnelId,
  managers,
  committedCrmManagerId,
  role,
  submitting = false,
  onSubmit,
}) => {
  const requiresManager = leadTaskCreateRequiresManagerId(role)
  const activeManagers = useMemo(
    () => managers.filter((m) => m.is_active && m.user?.id != null),
    [managers],
  )

  const defaultManagerUserId = useMemo(
    () => resolveManagerUserId(managers, committedCrmManagerId),
    [committedCrmManagerId, managers],
  )

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [deadline, setDeadline] = useState<Dayjs | null>(null)
  const [managerUserId, setManagerUserId] = useState<number | null>(null)
  const [titleError, setTitleError] = useState('')
  const [managerError, setManagerError] = useState('')

  useEffect(() => {
    if (!open) return
    setTitle('')
    setDescription('')
    setDeadline(null)
    setManagerUserId(defaultManagerUserId)
    setTitleError('')
    setManagerError('')
  }, [open, defaultManagerUserId])

  const managerOptions = useMemo(
    () =>
      activeManagers.map((m) => ({
        label: m.fio || m.user?.name || `Менеджер #${m.id}`,
        value: m.user.id,
      })),
    [activeManagers],
  )

  const handleSubmit = async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setTitleError('Укажите название')
      return
    }
    if (trimmedTitle.length > 255) {
      setTitleError('Максимум 255 символов')
      return
    }
    setTitleError('')

    if (requiresManager && (managerUserId == null || managerUserId <= 0)) {
      setManagerError('Выберите менеджера')
      return
    }
    setManagerError('')

    const body: CrmCreateLeadFunnelTaskRequest = {
      lead_funnel_id: leadFunnelId,
      title: trimmedTitle,
    }
    const desc = description.trim()
    if (desc) body.description = desc
    if (deadline?.isValid()) {
      body.deadline = deadline.format('YYYY-MM-DD')
    }
    if (requiresManager && managerUserId != null) {
      body.manager_id = managerUserId
    }

    const created = await onSubmit(body)
    if (created) onClose()
  }

  if (!open || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <Modal
      active={open}
      className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
      style={{ zIndex: String(CREATE_LEAD_TASK_MODAL_Z_INDEX) }}
      setActive={() => {
        if (submitting) return
        onClose()
      }}
    >
      <div className={cn(mclasses.modalContainer, classes.modalContainer)}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>Новая задача</span>
          </div>
          <div
            className={mclasses.closeIcon}
            onClick={() => {
              if (submitting) return
              onClose()
            }}
          >
            <IcoRemove />
          </div>
        </div>

        <div className={cn(mclasses.formWrapper, classes.createForm)}>
          <div>
            <p className={mclasses.label}>Название</p>
            {/* @ts-ignore */}
            <InputNew
              className={iclasses.newInputDesign}
              value={title}
              maxLength={255}
              placeholder="Например, Перезвонить"
              onChange={(e: any) => {
                setTitle(e.target.value)
                if (titleError) setTitleError('')
              }}
              validate={!!titleError}
              errorText={titleError}
              typeValid="custom"
              disabled={submitting}
            />
          </div>

          <div>
            <p className={mclasses.label}>Описание</p>
            {/* @ts-ignore */}
            <InputTextarea
              className={iclasses.newTextareaInputDesign}
              value={description}
              placeholder="Необязательно"
              onChange={(e: any) => setDescription(e.target.value)}
              validate={false}
              disabled={submitting}
              rows={3}
            />
          </div>

          <div>
            <p className={mclasses.label}>Срок</p>
            <DatePicker
              value={deadline}
              onChange={(value) => setDeadline(value)}
              format="DD.MM.YYYY"
              placeholder="Необязательно"
              allowClear
              disabled={submitting}
              className={classes.deadlinePicker}
              getPopupContainer={() => document.body}
            />
          </div>

          {requiresManager ? (
            <div>
              <p className={mclasses.label}>Менеджер (исполнитель)</p>
              <Select
                showSearch
                allowClear
                placeholder="Выберите менеджера"
                options={managerOptions}
                value={managerUserId ?? undefined}
                onChange={(value) => {
                  setManagerUserId(
                    value === undefined || value === null || value === ''
                      ? null
                      : Number(value),
                  )
                  if (managerError) setManagerError('')
                }}
                status={managerError ? 'error' : undefined}
                disabled={submitting}
                getPopupContainer={() => document.body}
              />
              {managerError ? (
                <p className={iclasses.customErrorText}>{managerError}</p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={mclasses.actions}>
          <Button color="gray" disabled={submitting} onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            isLoading={submitting}
            disabled={submitting}
            onClick={() => {
              void handleSubmit()
            }}
          >
            Создать
          </Button>
        </div>
      </div>
    </Modal>,
    document.body,
  )
}

export default CreateLeadTaskModal
